import {
    booleanAttribute,
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    input,
    output,
    signal
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { SiPillsInputComponent } from '@siemens/element-ng/pills-input';
import {
    SelectItem,
    SelectOption,
    SelectOptionSource,
    SiSelectComponent,
    SiSelectLazyOptionsDirective,
    SiSelectMultiValueDirective,
    SiSelectSingleValueDirective
} from '@siemens/element-ng/select';
import { ApplicationUser } from '@tran-monorepo/interfaces';
import { Observable, startWith } from 'rxjs';
import { CloseOnScrollDirective } from '../../../directives/close-on-scroll.directive';
import { PersonOptionSourceService } from '../../../services/person-option-source.service';
import { getApplicationUserDisplayName } from '../../../utils';

@Component({
    selector: 'tran-people-select',
    imports: [
        ReactiveFormsModule,
        SiPillsInputComponent,
        SiSelectComponent,
        SiSelectSingleValueDirective,
        SiSelectMultiValueDirective,
        SiSelectLazyOptionsDirective,
        TranslatePipe,
        CloseOnScrollDirective
    ],
    templateUrl: './people-select.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PeopleSelectComponent {
    private readonly personOptionSourceService = inject(PersonOptionSourceService);

    public readonly control = input.required<FormControl<ApplicationUser | ApplicationUser[] | null>>();
    public readonly multi = input(false, { transform: booleanAttribute });
    public readonly readonly = input(false, { transform: booleanAttribute });
    public readonly placeholder = input<string>('components.people-select.placeholder');
    public readonly ariaLabel = input<string>();
    public readonly openChange = output<boolean>();

    protected readonly peopleDisplayControl = new FormControl<string[]>([], { nonNullable: true });

    // Internes Signal das den aktuellen Wert trackt
    private readonly currentValue = signal<ApplicationUser | ApplicationUser[] | null>(null);

    protected readonly hasClearableValue = computed(() => {
        const value = this.currentValue();
        const hasValue = Array.isArray(value) ? value.length > 0 : value != null;
        return hasValue && !this.readonly();
    });

    constructor() {
        this.effectSyncControlValueChanges();
    }

    protected readonly personOptionsSource: SelectOptionSource<ApplicationUser> = {
        getOptionsForValues: (values: ApplicationUser[]): Observable<SelectOption<ApplicationUser>[]> => {
            return this.personOptionSourceService.getOptionsForValues(values);
        },
        getOptionsForSearch: (query: string): Observable<SelectItem<ApplicationUser>[]> => {
            return this.personOptionSourceService.getOptionsForSearch(query);
        }
    };

    protected onOpenChange(isOpen: boolean): void {
        this.openChange.emit(isOpen);
    }

    protected clearSelection(): void {
        this.control().setValue(null);
    }

    private effectSyncControlValueChanges(): void {
        effect(onCleanup => {
            const ctrl = this.control();

            const subscription = ctrl.valueChanges.pipe(startWith(ctrl.value)).subscribe(value => {
                // Wert ins Signal schreiben damit hasClearableValue reaktiv ist
                this.currentValue.set(value);

                const names = this.getPeopleNames(value);
                this.peopleDisplayControl.setValue(names, { emitEvent: false });
            });

            onCleanup(() => subscription.unsubscribe());
        });
    }

    private getPeopleNames(value: ApplicationUser | ApplicationUser[] | null): string[] {
        if (!value) return [];

        const people = Array.isArray(value) ? value : [value];
        return people.filter(person => person != null).map(person => getApplicationUserDisplayName(person));
    }
}
